/**
 * XUX Unified JSON schema form
 */
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  OnChanges,
  Type,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { first, get, has, invoke, isEmpty, isFunction, some } from 'lodash';

import { BehaviorSubject, rx, Subject, Subscription } from '@shared/rx';
import { XVIError } from '@shared/models';
import {
  IBaseEntityType,
  IFormGroupPreferences,
  IFormAction,
  XUX_FORM_ACTIONS,
  IIconOptions,
  ICommonFormAction,
} from '@shared/types';
import { controlValueObs, hasMethod } from '@shared/utils/common-utils';
import { WIDGET_FAMILY } from '@config/global-constants';
import { XVISchemaFormGroupComponent } from '../../form/schema-form-group/schema-form-group.component';
import { xuxDynamicComponentsMap } from '../xux-dynamic-widget-mapping';
import {
  ICommonFormConfig,
  IFormExpanded,
} from '@app/components/form/common-form/common-form.types';

const INITIAL_TAB_INDEX = 0;

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-schema-form',
  templateUrl: './xux-schema-form.component.html',
  styleUrls: ['xux-schema-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XuxXVISchemaFormComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @ViewChild('scrollArea', { static: false }) public scrollArea: ElementRef;
  @ViewChild('shemaFormGroup', { static: false })
  public shemaFormGroup: ElementRef;
  @ViewChildren(XVISchemaFormGroupComponent)
  public groupItems: QueryList<XVISchemaFormGroupComponent>;
  @Input() public dynamicWidgetsMap: Record<string, Type<any>> =
    xuxDynamicComponentsMap;
  @Input() public defaultWidgetFamily: string = WIDGET_FAMILY.XUX;
  @Input() public form: FormGroup;
  @Input() public groups: IFormGroupPreferences[];
  @Input() public errors: XVIError[];
  @Input() public documentSidebarDetails: Partial<IBaseEntityType>;
  @Input() public bannerText: string;
  @Input() public expandSidebar: boolean;
  @Input() public showSidebarCollapsedBtn: boolean;

  @Input() public enableRightExpand: boolean = false;
  @Input() public rightExpandConfig: IIconOptions = {};
  @Input() public rightFormsConfig: ICommonFormConfig[] = [];
  @Input() public rightExpanded$: BehaviorSubject<IFormExpanded>;

  @Input() public disabledFunc: () => boolean;

  /**
   * This input is optional
   */
  @Input() public groupIndexControl: FormControl;
  /**
   * optional beforeChange handler can postpone or even prevent active page from changing.
   * Return a promise from the handler to make form wait before change active tab or
   * return false | Promise <false> to prevent tab from changing and keep the current one.
   */
  @Input() public beforeChange: (
    index: number,
    group: IFormGroupPreferences,
  ) => boolean | Promise<boolean>;

  @Output() public onFormAction: EventEmitter<IFormAction> = new EventEmitter();
  @Output() public onRightFormAction: EventEmitter<ICommonFormAction> =
    new EventEmitter();

  public rightFormExpanded: boolean = false;

  public activeTabIndex: number = INITIAL_TAB_INDEX;
  public activeTabName: string = '';

  public groupItemsArray: XVISchemaFormGroupComponent[];
  public subscriptions: Subscription[] = [];

  private tabIndexControlSub: Subscription;
  private lastEmittedTabIndex: number;
  private formAction: Subject<IFormAction>;

  /**
   * busy flag is set to true during page change process
   * When set to true it should prevent
   */
  private busy: boolean;

  public constructor() {}

  public ngOnInit(): void {
    if (!this.groupIndexControl) {
      this.groupIndexControl = new FormControl(INITIAL_TAB_INDEX);
    }
    // eslint-disable-next-line import/no-deprecated
    this.tabIndexControlSub = controlValueObs(this.groupIndexControl)
      .pipe(rx.distinctUntilChanged())
      .subscribe(this.tabChanged);
  }

  public ngOnChanges(changes): void {
    if (has(changes, 'groups')) {
      if (!this.formAction) {
        this.initFormAction();
      }

      if (!this.groupIndexControl) {
        this.groupIndexControl = new FormControl(INITIAL_TAB_INDEX);
      }

      this.tabChanged();
    }
  }

  public ngAfterViewInit(): void {
    this.subscriptions.push(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      this.groupItems.changes.subscribe((elements) => {
        setTimeout(() => {
          this.groupItemsArray = this.groupItems.toArray();
          this.focus();
        });
      }),
    );
  }

  public focus(): void {
    if (Array.isArray(this.groupItemsArray) && !isEmpty(this.groupItemsArray)) {
      const groupItem = this.groupItemsArray.find(
        (item) => item.group.name === this.activeTabName,
      );
      setTimeout(() => {
        invoke(groupItem, 'focus');
      });
    }
  }

  public onRightFormExpandChanged(event: Record<string, boolean>): void {
    this.rightFormExpanded = some(event, (value) => value);
  }

  /**
   * returns true when provided index is equal to indexControl value
   */
  public isActiveTab(tabIndex: number): boolean {
    return tabIndex === get(this.groupIndexControl, 'value');
  }

  /**
   * Toggles the expanded collapse state
   */
  public expandCollapseSidebar(isCollapsed: boolean): void {
    this.expandSidebar = !isCollapsed;
  }

  /**
   * Sets input value to the index control.
   * This actions triggers tabChanged call among other possible subscriptions.
   * When beforeChange handler is defined it is being called prior to change active tab.
   */

  public async setActiveTabIndex(pageIndex: number): Promise<void> {
    if (this.busy) {
      return;
    }

    let pageChangeAllowed = true;

    if (isFunction(this.beforeChange)) {
      pageChangeAllowed = false;
      try {
        this.busy = true;
        pageChangeAllowed = await this.beforeChange(
          pageIndex,
          this.groups[pageIndex],
        );
      } catch (e) {
        pageChangeAllowed = false;
      } finally {
        this.busy = false;
      }
    }

    if (pageChangeAllowed) {
      this.groupIndexControl.setValue(pageIndex);
      this.activeTabIndex = pageIndex;
      this.activeTabName = get(this, ['groups', pageIndex, 'name'], '');
    }
  }

  /**
   * Safely unsubscribes from index control changes
   */
  public ngOnDestroy(): void {
    if (this.tabIndexControlSub) {
      this.tabIndexControlSub.unsubscribe();
      this.tabIndexControlSub = null;
    }

    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Called on any index control change, either internal or external.
   * Performing all actions necessary for a tab change
   */
  private tabChanged = (tabIndex: number = this.activeTabIndex): void => {
    this.activeTabName = get(this, ['groups', tabIndex, 'name'], '');
    if (get(this, ['scrollArea', 'nativeElement', 'scrollTop'])) {
      this.scrollArea.nativeElement.scrollTop = 0;
    }
    this.focus();

    this.emitSelectedGroupChangeAction(tabIndex);
  };

  private async emitSelectedGroupChangeAction(tabIndex: number): Promise<void> {
    if (this.lastEmittedTabIndex === tabIndex) return;
    this.lastEmittedTabIndex = tabIndex;
    if (this.formAction) {
      this.formAction.next({
        selectedGroupIndex: tabIndex,
        type: XUX_FORM_ACTIONS.FORM_SELECTED_GROUP_CHANGED,
      });
    }
  }

  /**
   * Createas a subscription for form actions to reemit actions as own events
   */
  private initFormAction(): void {
    this.formAction = get(first(this.groups), 'action');
    if (hasMethod(this.formAction, 'subscribe')) {
      this.subscriptions.push(
        this.formAction.subscribe((action) => {
          this.onFormAction.emit({
            ...action,
            formValue: this.form.value,
            selectedGroupIndex: this.groupIndexControl.value,
          });
        }),
      );
    }
  }
}
